import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as action from '../../store/actions/filled-questionnaire-action';
import ReCAPTCHA from "react-google-recaptcha";
import api from '../../utils/apiUtils';


type ParamsType = {
    mode: string;
};
  
const Landing = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const questionnaireId = useSelector((store: any) => store.questionnaire.id);
    const filledQuestionnaire = useSelector((store: any) => store.filledQuestionnaire);
    const {mode} = useParams<ParamsType>();
    const reRef = useRef<ReCAPTCHA>(null);

    let navigate = useNavigate();
    let dispatch = useDispatch();

    // Helper function to get cached data with expiration check
    const getCachedFormData = () => {
        const cached = localStorage.getItem('landingFormData');
        if (cached) {
            const { data, timestamp } = JSON.parse(cached);
            const now = new Date().getTime();
            const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds

            if (now - timestamp < fiveMinutes) {
                return data;
            } else {
                // Clear expired cache
                localStorage.removeItem('landingFormData');
            }
        }
        return null;
    };

    const initialFormData: {[index: string]:any} = getCachedFormData() || {
        questionnairesId: questionnaireId,
        fullName: '',
        companyName: '',
        email: '',
        role: '',
        companySize: '',
        industry: '',
        country: ''
    };


    const [formData, setFormData] = useState(initialFormData);
    const [submitted, setSubmitted] = useState(false);
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    window.onload = function() {
        window.location.pathname = '/'
    };
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
            setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isDropdownOpen) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [isDropdownOpen]);

    useEffect(() => {
        if (mode === 'edit') {
            setFormData({...filledQuestionnaire});
        }
      }, [ mode, filledQuestionnaire]);

    // Save form data to localStorage whenever it changes
    useEffect(() => {
        // Don't cache if in edit mode or if form is empty
        if (mode !== 'edit' && Object.values(formData).some(value => value && value !== questionnaireId)) {
            const dataToCache = {
                data: formData,
                timestamp: new Date().getTime()
            };
            localStorage.setItem('landingFormData', JSON.stringify(dataToCache));
        }
    }, [formData, mode, questionnaireId]);

    const previousClick = () => {
        navigate('/');
    }

    const handleChange = (e: any) => {
        setFormData((prevState: any) => {
         return { ...prevState,
              [e.target.name]: e.target.value
            }
        });
        // Reset submitted state when user starts typing (this will hide errors)
        if (submitted) {
            setSubmitted(false);
        }
    };

    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setSubmitted(true);

        // Manual validation - check if required fields are empty or contain only whitespace
        const requiredFields = ['fullName', 'companyName', 'email', 'role', 'companySize', 'industry', 'country'];
        const hasEmptyFields = requiredFields.some(field => !formData[field] || formData[field].trim() === '');

        // Email format validation (also trim whitespace)
        const hasInvalidEmail = formData.email.trim() && !isValidEmail(formData.email.trim());

        if (hasEmptyFields || hasInvalidEmail) {
            // Don't submit if there are empty required fields or invalid email
            return;
        }

        // Check if ReCAPTCHA is enabled via environment variable
        const isRecaptchaEnabled = process.env.REACT_APP_ENABLE_RECAPTCHA === 'true';

        try {
            if (isRecaptchaEnabled) {
                // Production mode - use ReCAPTCHA
                console.log('Using ReCAPTCHA for form submission...');

                if (!reRef.current) {
                    console.error('ReCAPTCHA ref not available');
                    return;
                }

                const token = await reRef.current.executeAsync();
                if (!token) {
                    console.error('Failed to get ReCAPTCHA token');
                    return;
                }

                await api.post('/auth/recaptcha', {token: token});
                dispatch({type: action.ADD_QUESTIONNAIRE, payload: formData});
                // Clear cached form data on successful submission
                localStorage.removeItem('landingFormData');
                navigate('/questionnaire');

            } else {
                // Development/Test mode - bypass ReCAPTCHA
                console.log('Development mode: Bypassing ReCAPTCHA...');

                // Use a dummy token for development
                const token = 'development-token-' + Date.now();

                // Try to validate with backend, but proceed even if it fails
                try {
                    await api.post('/auth/recaptcha', {token: token});
                } catch (error) {
                    console.log('ReCAPTCHA validation skipped in development mode');
                }

                dispatch({type: action.ADD_QUESTIONNAIRE, payload: formData});
                // Clear cached form data on successful submission
                localStorage.removeItem('landingFormData');
                navigate('/questionnaire');
            }
        } catch (error) {
            console.error('Form submission error:', error);

            // In development mode, proceed anyway
            if (!isRecaptchaEnabled) {
                dispatch({type: action.ADD_QUESTIONNAIRE, payload: formData});
                // Clear cached form data on successful submission
                localStorage.removeItem('landingFormData');
                navigate('/questionnaire');
            }
        }
      };

    return (
        <>
            <style>{`
                body, html {
                    margin: 0;
                    padding: 0;
                    background-color: #1C1D1F !important;
                    overflow-x: hidden;
                }
                * {
                    box-sizing: border-box;
                }

                .error-input::placeholder {
                        color: #E53E3E !important;
                    }

                /* Prevent autofill styling */
                input:-webkit-autofill,
                input:-webkit-autofill:hover,
                input:-webkit-autofill:focus,
                input:-webkit-autofill:active {
                    -webkit-box-shadow: 0 0 0 30px #36363A inset !important;
                    -webkit-text-fill-color: #A7A9BE !important;
                    background-color: #36363A !important;
                    border: 1px solid #27272A !important;
                    border-radius: 8px !important;
                }

                /* For error state autofill */
                input.error-input:-webkit-autofill,
                input.error-input:-webkit-autofill:hover,
                input.error-input:-webkit-autofill:focus,
                input.error-input:-webkit-autofill:active {
                    -webkit-box-shadow: 0 0 0 30px #36363A inset !important;
                    -webkit-text-fill-color: #E53E3E !important;
                    background-color: #36363A !important;
                    border: 1px solid #E53E3E !important;
                    border-radius: 8px !important;
                }
            `}</style>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: isMobile ? '20px' : isTablet ? '40px' : '64px',
                    gap: '40px',
                    minHeight: '100vh',
                    width: '100vw',
                    backgroundColor: '#1C1D1F',
                    boxSizing: 'border-box',
                    position: 'relative',
                    left: '0',
                    top: '0'
                }}
            >
            <div
                style={{
                    display: 'flex',
                    flexDirection: (isMobile || isTablet) ? 'column' : 'row',
                    alignItems: (isMobile || isTablet) ? 'center' : 'flex-start',
                    padding: '0px',
                    gap: isMobile ? '40px' : isTablet ? '56px' : '72px',
                    width: '100%',
                    maxWidth: '1260px'
                }}
            >
                {/* Left Column - Content */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: (isMobile || isTablet) ? 'center' : 'flex-start',
                        padding: '0px',
                        gap: isMobile ? '40px' : isTablet ? '60px' : '80px',
                        width: (isMobile || isTablet) ? '100%' : '573px',
                        height: (isMobile || isTablet) ? 'auto' : '438px',
                        flex: 'none',
                        order: 0,
                        flexGrow: (isMobile || isTablet) ? 0 : 1,
                        textAlign: (isMobile || isTablet) ? 'center' : 'left'
                    }}
                >
                    {/* Header section */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: (isMobile || isTablet) ? 'center' : 'flex-start',
                            padding: '0px',
                            gap: isMobile ? '24px' : isTablet ? '50px' : '90px',
                            width: '100%',
                            maxWidth: (isMobile || isTablet) ? '100%' : '573px',
                            height: 'auto',
                            flex: 'none',
                            order: 0,
                            alignSelf: 'stretch',
                            flexGrow: 0
                        }}
                    >
                        <h1
                            style={{
                                width: '100%',
                                height: 'auto',
                                fontFamily: 'Archivo',
                                fontStyle: 'normal',
                                fontWeight: 700,
                                fontSize: isMobile ? '28px' : isTablet ? '36px' : '44px',
                                lineHeight: '130%',
                                color: '#EAEEF4',
                                flex: 'none',
                                order: 0,
                                alignSelf: 'stretch',
                                flexGrow: 0,
                                margin: 0,
                                textAlign: (isMobile || isTablet) ? 'center' : 'left'
                            }}
                        >
                            Get to know each other!
                        </h1>
                        <p
                            style={{
                                width: '100%',
                                height: 'auto',
                                fontFamily: 'Inter',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                fontSize: isMobile ? '14px' : isTablet ? '15px' : '16px',
                                lineHeight: '150%',
                                fontFeatureSettings: "'calt' off",
                                color: '#A7A9BE',
                                flex: 'none',
                                order: 1,
                                alignSelf: 'stretch',
                                flexGrow: 0,
                                margin: 0,
                                textAlign: (isMobile || isTablet) ? 'center' : 'left'
                            }}
                        >
                            By evaluating your data infrastructure, team capabilities, analytics practices, and AI implementation, we can determine which of our tailored solutions will best accelerate your progress.
                        </p>
                    </div>

                    {/* Contact info and social media */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: (isMobile || isTablet) ? 'center' : 'flex-start',
                            padding: '0px',
                            gap: isMobile ? '40px' : '80px',
                            width: isMobile ? '100%' : '141px',
                            height: 'auto',
                            flex: 'none',
                            order: 1,
                            flexGrow: 0
                        }}
                    >
                        <a
                            href="mailto:info@smartcat.io"
                            style={{
                                width: (isMobile || isTablet) ? 'auto' : '131px',
                                height: '24px',
                                fontFamily: 'Inter',
                                fontStyle: 'normal',
                                fontWeight: 500,
                                fontSize: isMobile ? '14px' : isTablet ? '15px' : '16px',
                                lineHeight: '150%',
                                fontFeatureSettings: "'calt' off",
                                color: '#249FDA',
                                textDecoration: 'none',
                                flex: 'none',
                                order: 0,
                                flexGrow: 0,
                                textAlign: (isMobile || isTablet) ? 'center' : 'left'
                            }}
                        >
                            info@smartcat.io
                        </a>

                        {/* Social media icons */}
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: (isMobile || isTablet) ? 'center' : 'flex-start',
                                alignItems: 'center',
                                padding: '0px',
                                gap: '15px',
                                width: (isMobile || isTablet) ? 'auto' : '141px',
                                height: '24px',
                                flex: 'none',
                                order: 2,
                                flexGrow: 0
                            }}
                        >
                            {/* Facebook */}
                            <a href="https://www.facebook.com/SmartCat.io" target="_blank" rel="noopener noreferrer" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="10" height="19" viewBox="0 0 10 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9.5 3.5H6.5C5.9477 3.5 5.5 3.94772 5.5 4.5V7.5H9.5C9.6137 7.49748 9.7216 7.5504 9.7892 7.6419C9.8568 7.7334 9.8758 7.852 9.84 7.96L9.1 10.16C9.0318 10.3619 8.8431 10.4984 8.63 10.5H5.5V18C5.5 18.2761 5.2761 18.5 5 18.5H2.5C2.22386 18.5 2 18.2761 2 18V10.5H0.5C0.22386 10.5 0 10.2761 0 10V8C0 7.7239 0.22386 7.5 0.5 7.5H2V4.5C2 2.29086 3.7909 0.5 6 0.5H9.5C9.7761 0.5 10 0.72386 10 1V3C10 3.27614 9.7761 3.5 9.5 3.5Z" fill="#A7A9BE"/>
                                </svg>
                            </a>

                            {/* Instagram */}
                            <a href="https://www.instagram.com/smartcat.io/" target="_blank" rel="noopener noreferrer" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M13 0.5H5C2.23858 0.5 0 2.73858 0 5.5V13.5C0 16.2614 2.23858 18.5 5 18.5H13C15.7614 18.5 18 16.2614 18 13.5V5.5C18 2.73858 15.7614 0.5 13 0.5ZM16.25 13.5C16.2445 15.2926 14.7926 16.7445 13 16.75H5C3.20735 16.7445 1.75549 15.2926 1.75 13.5V5.5C1.75549 3.70735 3.20735 2.25549 5 2.25H13C14.7926 2.25549 16.2445 3.70735 16.25 5.5V13.5ZM13.75 5.75C14.3023 5.75 14.75 5.30228 14.75 4.75C14.75 4.19772 14.3023 3.75 13.75 3.75C13.1977 3.75 12.75 4.19772 12.75 4.75C12.75 5.30228 13.1977 5.75 13.75 5.75ZM9 5C6.51472 5 4.5 7.01472 4.5 9.5C4.5 11.9853 6.51472 14 9 14C11.4853 14 13.5 11.9853 13.5 9.5C13.5027 8.3057 13.0294 7.15957 12.1849 6.31508C11.3404 5.47059 10.1943 4.99734 9 5ZM6.25 9.5C6.25 11.0188 7.4812 12.25 9 12.25C10.5188 12.25 11.75 11.0188 11.75 9.5C11.75 7.9812 10.5188 6.75 9 6.75C7.4812 6.75 6.25 7.9812 6.25 9.5Z" fill="#A7A9BE"/>
                                </svg>
                            </a>

                            {/* The X */}
                            <a href="https://x.com/SmartCat_io" target="_blank" rel="noopener noreferrer" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.1328 17.3478L11.264 8.12469L17.0549 1.75438C17.1859 1.60674 17.2534 1.41343 17.2428 1.21636C17.2322 1.01929 17.1444 0.834346 16.9983 0.701615C16.8523 0.568884 16.6598 0.49907 16.4626 0.507309C16.2654 0.515547 16.0794 0.601174 15.9449 0.745625L10.4287 6.81312L6.63276 0.847813C6.56507 0.74127 6.47158 0.653535 6.36097 0.592735C6.25035 0.531936 6.12617 0.500039 5.99994 0.5H1.49994C1.36547 0.499935 1.23345 0.536026 1.11771 0.604494C1.00198 0.672962 0.906774 0.771288 0.842079 0.889177C0.777384 1.00707 0.745575 1.14018 0.749983 1.27458C0.75439 1.40898 0.794852 1.53973 0.867132 1.65313L6.73588 10.8753L0.944945 17.2503C0.87732 17.323 0.824785 17.4083 0.790386 17.5014C0.755987 17.5945 0.740407 17.6935 0.74455 17.7927C0.748693 17.8918 0.772477 17.9892 0.814522 18.0791C0.856566 18.169 0.916036 18.2496 0.989485 18.3164C1.06293 18.3831 1.1489 18.4346 1.2424 18.4679C1.3359 18.5012 1.43508 18.5156 1.53419 18.5103C1.63329 18.5049 1.73035 18.48 1.81974 18.4369C1.90914 18.3937 1.98908 18.3333 2.05494 18.2591L7.5712 12.1916L11.3671 18.1569C11.4354 18.2625 11.5291 18.3494 11.6397 18.4093C11.7503 18.4693 11.8742 18.5005 11.9999 18.5H16.4999C16.6343 18.5 16.7661 18.4638 16.8817 18.3954C16.9973 18.327 17.0924 18.2288 17.1571 18.111C17.2218 17.9933 17.2536 17.8604 17.2493 17.7261C17.2451 17.5918 17.2048 17.4612 17.1328 17.3478ZM12.4115 17L2.86588 2H5.58463L15.134 17H12.4115Z" fill="#A7A9BE"/>
                                </svg>
                            </a>

                            {/* LinkedIn */}
                            <a href="https://www.linkedin.com/company/smartcat-io/" target="_blank" rel="noopener noreferrer" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M2 0.5H16C17.1046 0.5 18 1.39543 18 2.5V16.5C18 17.6046 17.1046 18.5 16 18.5H2C0.89543 18.5 0 17.6046 0 16.5V2.5C0 1.39543 0.89543 0.5 2 0.5ZM5 15.5C5.27614 15.5 5.5 15.2761 5.5 15V8C5.5 7.7239 5.27614 7.5 5 7.5H3.5C3.22386 7.5 3 7.7239 3 8V15C3 15.2761 3.22386 15.5 3.5 15.5H5ZM4.25 6.5C3.42157 6.5 2.75 5.82843 2.75 5C2.75 4.17157 3.42157 3.5 4.25 3.5C5.07843 3.5 5.75 4.17157 5.75 5C5.75 5.82843 5.07843 6.5 4.25 6.5ZM14.5 15.5C14.7761 15.5 15 15.2761 15 15V10.4C15.0325 8.8108 13.8576 7.45452 12.28 7.26C11.177 7.15925 10.1083 7.6744 9.5 8.6V8C9.5 7.7239 9.2761 7.5 9 7.5H7.5C7.2239 7.5 7 7.7239 7 8V15C7 15.2761 7.2239 15.5 7.5 15.5H9C9.2761 15.5 9.5 15.2761 9.5 15V11.25C9.5 10.4216 10.1716 9.75 11 9.75C11.8284 9.75 12.5 10.4216 12.5 11.25V15C12.5 15.2761 12.7239 15.5 13 15.5H14.5Z" fill="#A7A9BE"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>

                {/* Right Column - Form */}
                <form onSubmit={handleSubmit} noValidate
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        padding: isMobile ? '24px' : isTablet ? '32px' : '40px',
                        gap: isMobile ? '20px' : isTablet ? '24px' : '30px',
                        width: (isMobile || isTablet) ? '100%' : '615px',
                        maxWidth: isTablet ? '600px' : 'none',
                        minHeight: 'auto',
                        background: '#27272A',
                        borderRadius: '16px',
                        flex: 'none',
                        order: 1,
                        flexGrow: 0,
                        boxSizing: 'border-box',
                        justifyContent: 'space-between',
                        margin: '0 0 40px 0'
                    }}
                >
                    {/* Conditionally render ReCAPTCHA based on environment */}
                    {process.env.REACT_APP_ENABLE_RECAPTCHA === 'true' && (
                        <ReCAPTCHA
                            sitekey={process.env.REACT_APP_RECAPTCHA_PUBLIC_KEY || ''}
                            size='invisible'
                            ref={reRef}
                        />
                    )}

                    {/* Form fields container */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            padding: '0px',
                            gap: isMobile ? '20px' : isTablet ? '22px' : '24px',
                            width: '100%',
                            height: 'auto',
                            flex: 'none',
                            order: 0,
                            alignSelf: 'stretch',
                            flexGrow: 0
                        }}
                    >
                        {/* Full name field with error state */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            padding: '0px',
                            gap: '6px',
                            width: '100%',
                            height: 'auto',
                            flex: 'none',
                            order: 0,
                            alignSelf: 'stretch',
                            flexGrow: 0
                        }}>
                            <label style={{
                                fontFamily: 'Inter',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                fontSize: isMobile ? '13px' : isTablet ? '14px' : '14px',
                                lineHeight: '150%',
                                fontFeatureSettings: "'calt' off",
                                color: '#A7A9BE'
                            }}>
                                Full name
                            </label>
                            <input
                                type="text"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleChange}
                                placeholder="Your full name"
                                className={(!formData.fullName || formData.fullName.trim() === '') && submitted ? 'error-input' : ''}
                                style={{
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                    padding: isMobile ? '12px' : isTablet ? '14px' : '15px',
                                    gap: '16px',
                                    width: '100%',
                                    height: isMobile ? '48px' : isTablet ? '50px' : '54px',
                                    background: '#36363A',
                                    border: (!formData.fullName || formData.fullName.trim() === '') && submitted ? '1px solid #E53E3E' : '1px solid #27272A',
                                    borderRadius: '8px',
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isMobile ? '14px' : '16px',
                                    lineHeight: '150%',
                                    fontFeatureSettings: "'calt' off",
                                    color: (!formData.fullName || formData.fullName.trim() === '') && submitted ? '#E53E3E' : '#FFFFFF',
                                }}
                            />
                            {/* Error message */}
                            {(!formData.fullName || formData.fullName.trim() === '') && submitted && (
                                <span style={{
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isMobile ? '12px' : isTablet ? '13px' : '14px',
                                    lineHeight: '150%',
                                    fontFeatureSettings: "'calt' off",
                                    color: '#E53E3E'
                                }}>
                                    Please fill out this field.
                                </span>
                            )}
                        </div>

                        {/* Company name field */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '0px', gap: '6px', width: '100%', height: 'auto', flex: 'none', order: 1, alignSelf: 'stretch', flexGrow: 0 }}>
                            <label style={{ fontFamily: 'Inter', fontStyle: 'normal', fontWeight: 400, fontSize: '14px', lineHeight: '150%', fontFeatureSettings: "'calt' off", color: '#A7A9BE' }}>Company name</label>
                            <input
                                type="text"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleChange}
                                placeholder="Enter company name"
                                className={(!formData.companyName || formData.companyName.trim() === '') && submitted ? 'error-input' : ''}
                                style={{
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                    padding: isMobile ? '12px' : isTablet ? '14px' : '15px',
                                    gap: '16px',
                                    width: '100%',
                                    height: isMobile ? '48px' : isTablet ? '50px' : '54px',
                                    background: '#36363A',
                                    border: (!formData.companyName || formData.companyName.trim() === '') && submitted ? '1px solid #E53E3E' : '1px solid #27272A',
                                    borderRadius: '8px',
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isMobile ? '14px' : '16px',
                                    lineHeight: '150%',
                                    fontFeatureSettings: "'calt' off",
                                    color: (!formData.companyName || formData.companyName.trim() === '') && submitted ? '#E53E3E' : '#FFFFFF',
                                }}
                            />
                            {/* Error message */}
                            {(!formData.companyName || formData.companyName.trim() === '') && submitted && (
                                <span style={{
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isMobile ? '12px' : isTablet ? '13px' : '14px',
                                    lineHeight: '150%',
                                    fontFeatureSettings: "'calt' off",
                                    color: '#E53E3E'
                                }}>
                                    Please fill out this field.
                                </span>
                            )}
                        </div>

                        {/* Email field */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '0px', gap: '6px', width: '100%', height: 'auto', flex: 'none', order: 2, alignSelf: 'stretch', flexGrow: 0 }}>
                            <label style={{ fontFamily: 'Inter', fontStyle: 'normal', fontWeight: 400, fontSize: '14px', lineHeight: '150%', fontFeatureSettings: "'calt' off", color: '#A7A9BE' }}>Email address</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="example@email.com"
                                className={((!formData.email || formData.email.trim() === '') && submitted) || (formData.email && !isValidEmail(formData.email.trim()) && submitted) ? 'error-input' : ''}
                                style={{
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                    padding: isMobile ? '12px' : isTablet ? '14px' : '15px',
                                    gap: '16px',
                                    width: '100%',
                                    height: isMobile ? '48px' : isTablet ? '50px' : '54px',
                                    background: '#36363A',
                                    border: ((!formData.email || formData.email.trim() === '') && submitted) || (formData.email && !isValidEmail(formData.email.trim()) && submitted) ? '1px solid #E53E3E' : '1px solid #27272A',
                                    borderRadius: '8px',
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isMobile ? '14px' : '16px',
                                    lineHeight: '150%',
                                    fontFeatureSettings: "'calt' off",
                                    color: ((!formData.email || formData.email.trim() === '') && submitted) || (formData.email && !isValidEmail(formData.email.trim()) && submitted) ? '#E53E3E' : '#FFFFFF',
                                }}
                            />
                            {/* Error message */}
                            {(!formData.email || formData.email.trim() === '') && submitted && (
                                <span style={{
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isMobile ? '12px' : isTablet ? '13px' : '14px',
                                    lineHeight: '150%',
                                    fontFeatureSettings: "'calt' off",
                                    color: '#E53E3E'
                                }}>
                                    Please fill out this field.
                                </span>
                            )}
                            {formData.email && !isValidEmail(formData.email.trim()) && submitted && (
                                <span style={{
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isMobile ? '12px' : isTablet ? '13px' : '14px',
                                    lineHeight: '150%',
                                    fontFeatureSettings: "'calt' off",
                                    color: '#E53E3E'
                                }}>
                                    Please enter a valid email address.
                                </span>
                            )}
                        </div>

                        {/* Role field */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '0px', gap: '6px', width: '100%', height: 'auto', flex: 'none', order: 3, alignSelf: 'stretch', flexGrow: 0 }}>
                            <label style={{ fontFamily: 'Inter', fontStyle: 'normal', fontWeight: 400, fontSize: '14px', lineHeight: '150%', fontFeatureSettings: "'calt' off", color: '#A7A9BE' }}>Role</label>
                            <input
                                type="text"
                                name="role"
                                value={formData.role}
                                onChange={handleChange}
                                placeholder="Enter role"
                                className={(!formData.role || formData.role.trim() === '') && submitted ? 'error-input' : ''}
                                style={{
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                    padding: isMobile ? '12px' : isTablet ? '14px' : '15px',
                                    gap: '16px',
                                    width: '100%',
                                    height: isMobile ? '48px' : isTablet ? '50px' : '54px',
                                    background: '#36363A',
                                    border: (!formData.role || formData.role.trim() === '') && submitted ? '1px solid #E53E3E' : '1px solid #27272A',
                                    borderRadius: '8px',
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isMobile ? '14px' : '16px',
                                    lineHeight: '150%',
                                    fontFeatureSettings: "'calt' off",
                                    color: (!formData.role || formData.role.trim() === '') && submitted ? '#E53E3E' : '#FFFFFF',
                                }}
                            />
                            {/* Error message */}
                            {(!formData.role || formData.role.trim() === '') && submitted && (
                                <span style={{
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isMobile ? '12px' : isTablet ? '13px' : '14px',
                                    lineHeight: '150%',
                                    fontFeatureSettings: "'calt' off",
                                    color: '#E53E3E'
                                }}>
                                    Please fill out this field.
                                </span>
                            )}
                        </div>

                        {/* Company size field */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '0px', gap: '6px', width: '100%', height: 'auto', flex: 'none', order: 4, alignSelf: 'stretch', flexGrow: 0 }}>
                            <label style={{ fontFamily: 'Inter', fontStyle: 'normal', fontWeight: 400, fontSize: '14px', lineHeight: '150%', fontFeatureSettings: "'calt' off", color: '#A7A9BE' }}>Company size</label>
                            <div style={{ position: 'relative', width: '100%', height: '54px' }}>
                                <div
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    style={{
                                        boxSizing: 'border-box',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '15px',
                                        gap: '15px',
                                        width: '100%',
                                        height: '54px',
                                        background: '#36363A',
                                        border: formData.companySize === '' && submitted ? '1px solid #E53E3E' : '1px solid #27272A',
                                        borderRadius: '8px',
                                        fontFamily: 'Inter',
                                        fontStyle: 'normal',
                                        fontWeight: 500,
                                        fontSize: isMobile ? '14px' : '16px',
                                        lineHeight: '150%',
                                        color: formData.companySize ? '#FFFFFF' : '#A7A9BE',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <span>{formData.companySize || 'Select'}</span>
                                    <div style={{
                                        width: '24px',
                                        height: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{
                                            transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s ease'
                                        }}>
                                            <path d="M3 4.5L6 7.5L9 4.5" stroke="#A7A9BE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                </div>

                                {isDropdownOpen && (
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        padding: '10px 0px',
                                        position: 'absolute',
                                        width: '100%',
                                        background: '#36363A',
                                        boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.25)',
                                        borderRadius: '8px',
                                        flex: 'none',
                                        order: 2,
                                        flexGrow: 0,
                                        zIndex: 10,
                                        top: '58px',
                                        left: '0'
                                    }}>
                                        {['1-10 employees', '11-50 employees', '51-200 employees', '201-1000 employees', '1000+ employees'].map((option, index) => (
                                            <div
                                                key={index}
                                                onClick={() => {
                                                    setFormData({...formData, companySize: option});
                                                    setIsDropdownOpen(false);
                                                }}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px 15px',
                                                    fontFamily: 'Inter',
                                                    fontStyle: 'normal',
                                                    fontWeight: 400,
                                                    fontSize: isMobile ? '14px' : '16px',
                                                    lineHeight: '150%',
                                                    color: '#FFFFFF',
                                                    cursor: 'pointer',
                                                    backgroundColor: 'transparent',
                                                    transition: 'background-color 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#4A4A4F';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                }}
                                            >
                                                {option}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {/* Error message */}
                            {formData.companySize === '' && submitted && (
                                <span style={{
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isMobile ? '12px' : isTablet ? '13px' : '14px',
                                    lineHeight: '150%',
                                    fontFeatureSettings: "'calt' off",
                                    color: '#E53E3E'
                                }}>
                                    Please fill out this field.
                                </span>
                            )}
                        </div>

                        {/* Industry field */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '0px', gap: '6px', width: '100%', height: 'auto', flex: 'none', order: 5, alignSelf: 'stretch', flexGrow: 0 }}>
                            <label style={{ fontFamily: 'Inter', fontStyle: 'normal', fontWeight: 400, fontSize: '14px', lineHeight: '150%', fontFeatureSettings: "'calt' off", color: '#A7A9BE' }}>Industry</label>
                            <input
                                type="text"
                                name="industry"
                                value={formData.industry}
                                onChange={handleChange}
                                placeholder="Enter industry"
                                className={(!formData.industry || formData.industry.trim() === '') && submitted ? 'error-input' : ''}
                                style={{
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                    padding: isMobile ? '12px' : isTablet ? '14px' : '15px',
                                    gap: '16px',
                                    width: '100%',
                                    height: isMobile ? '48px' : isTablet ? '50px' : '54px',
                                    background: '#36363A',
                                    border: (!formData.industry || formData.industry.trim() === '') && submitted ? '1px solid #E53E3E' : '1px solid #27272A',
                                    borderRadius: '8px',
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isMobile ? '14px' : '16px',
                                    lineHeight: '150%',
                                    fontFeatureSettings: "'calt' off",
                                    color: (!formData.industry || formData.industry.trim() === '') && submitted ? '#E53E3E' : '#FFFFFF',
                                }}
                            />
                            {/* Error message */}
                            {(!formData.industry || formData.industry.trim() === '') && submitted && (
                                <span style={{
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isMobile ? '12px' : isTablet ? '13px' : '14px',
                                    lineHeight: '150%',
                                    fontFeatureSettings: "'calt' off",
                                    color: '#E53E3E'
                                }}>
                                    Please fill out this field.
                                </span>
                            )}
                        </div>

                        {/* Country field */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '0px', gap: '6px', width: '100%', height: 'auto', flex: 'none', order: 6, alignSelf: 'stretch', flexGrow: 0 }}>
                            <label style={{ fontFamily: 'Inter', fontStyle: 'normal', fontWeight: 400, fontSize: '14px', lineHeight: '150%', fontFeatureSettings: "'calt' off", color: '#A7A9BE' }}>Country</label>
                            <input
                                type="text"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                placeholder="Enter country"
                                className={(!formData.country || formData.country.trim() === '') && submitted ? 'error-input' : ''}
                                style={{
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                    padding: isMobile ? '12px' : isTablet ? '14px' : '15px',
                                    gap: '16px',
                                    width: '100%',
                                    height: isMobile ? '48px' : isTablet ? '50px' : '54px',
                                    background: '#36363A',
                                    border: (!formData.country || formData.country.trim() === '') && submitted ? '1px solid #E53E3E' : '1px solid #27272A',
                                    borderRadius: '8px',
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isMobile ? '14px' : '16px',
                                    lineHeight: '150%',
                                    fontFeatureSettings: "'calt' off",
                                    color: (!formData.country || formData.country.trim() === '') && submitted ? '#E53E3E' : '#FFFFFF',
                                }}
                            />
                            {/* Error message */}
                            {(!formData.country || formData.country.trim() === '') && submitted && (
                                <span style={{
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isMobile ? '12px' : isTablet ? '13px' : '14px',
                                    lineHeight: '150%',
                                    fontFeatureSettings: "'calt' off",
                                    color: '#E53E3E'
                                }}>
                                    Please fill out this field.
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Terms Checkbox */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        padding: '0px',
                        gap: '12px',
                        width: '100%'
                    }}>
                        <input
                            type="checkbox"
                            id="terms"
                            checked={agreeToTerms}
                            onChange={(e) => setAgreeToTerms(e.target.checked)}
                        />
                        <label
                            htmlFor="terms"
                            style={{
                                fontFamily: 'Inter',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                fontSize: isMobile ? '13px' : isTablet ? '14px' : '14px',
                                lineHeight: '150%',
                                color: '#A7A9BE',
                                flex: 1,
                                cursor: 'pointer'
                            }}
                        >
                            I agree to SmartCat's{' '}
                            <a href="https://smartcat.io/privacy" target="_blank" rel="noopener noreferrer"
                                style={{ color: '#249FDA', textDecoration: 'none' }}>
                                Privacy Policy
                            </a>.
                        </label>
                    </div>

                    {/* Buttons */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            justifyContent: isMobile ? 'center' : 'flex-end',
                            alignItems: 'center',
                            padding: isMobile ? '16px 0px 0px' : isTablet ? '12px 0px 0px' : '10px 0px 0px',
                            gap: isMobile ? '12px' : isTablet ? '18px' : '22px',
                            width: '100%',
                            height: 'auto',
                            flex: 'none',
                            order: 1,
                            alignSelf: 'stretch',
                            flexGrow: 0
                        }}
                    >
                        {/* Previous button */}
                        <button
                            type="button"
                            onClick={previousClick}
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: isMobile ? '12px 24px' : isTablet ? '14px 40px' : '16px 51px',
                                gap: '2px',
                                width: isMobile ? '100%' : isTablet ? '150px' : '168px',
                                height: isMobile ? '48px' : isTablet ? '52px' : '56px',
                                background: '#36363A',
                                backdropFilter: 'blur(4px)',
                                borderRadius: '8px',
                                border: 'none',
                                cursor: 'pointer',
                                fontFamily: 'Inter',
                                fontStyle: 'normal',
                                fontWeight: 500,
                                fontSize: isMobile ? '14px' : isTablet ? '15px' : '16px',
                                lineHeight: '150%',
                                fontFeatureSettings: "'calt' off",
                                color: '#FFFFFF'
                            }}
                        >
                            Previous
                        </button>

                        {/* Continue button */}
                        <button
                            type="submit"
                            disabled={!agreeToTerms}
                            style={{
                                boxSizing: 'border-box',
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: isMobile ? '12px 24px' : isTablet ? '14px 40px' : '16px 51px',
                                gap: '2px',
                                width: isMobile ? '100%' : isTablet ? '150px' : '172px',
                                height: isMobile ? '48px' : isTablet ? '52px' : '56px',
                                background: !agreeToTerms ? '#36363A' : 'linear-gradient(91.9deg, #145F96 0.8%, #249FDA 99.45%)',
                                borderRadius: '8px',
                                border: 'none',
                                cursor: !agreeToTerms ? 'not-allowed' : 'pointer',
                                opacity: !agreeToTerms ? 0.5 : 1,
                                fontFamily: 'Inter',
                                fontStyle: 'normal',
                                fontWeight: 500,
                                fontSize: isMobile ? '14px' : isTablet ? '15px' : '16px',
                                lineHeight: '150%',
                                fontFeatureSettings: "'calt' off",
                                color: '#FFFFFF'
                            }}
                        >
                            Continue
                        </button>
                    </div>
                </form>
            </div>
        </div>
        </>
    )
}

export default Landing;